import React from 'react'
import { RenderNode } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import { Button, LinkButton } from '@/atoms/Button'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { EyebrowSM, HeadingXS, ParagraphMD, TitleMD, TitleXS } from '@/atoms/Text'
import { AngelFundingLink } from '@/molecules/InvestLink'
import { Torch } from '@/services/TorchesService'
import { Project } from '@/types/codegen-federation'
import { ICampaignAssets } from '@/types/generated-contentful'
import { useSafeAnalytics } from '@/utils/analytics'
import { formatNumberForDisplay } from '@/utils/numbers'
import { useTranslate } from '@/utils/translate/translate-client'
import TextBlock from '@/views/TorchView/Dynamic/TextBlock'
import CardWrapper from './CardWrapper'

const shortDescriptionOverride: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_, children) => (
    <ParagraphMD color="white" className="lg:photon-paragraph-lg line-clamp-2 @4xl:line-clamp-none">
      {children}
    </ParagraphMD>
  ),
}

interface Props {
  campaignAssets: ICampaignAssets[]
  isInvesting?: boolean
  layout?: 'fixed' | 'responsive'
  projectExpressingInterest?: Project
  torch?: Torch
}

const TorchCard: React.FC<Props> = ({
  campaignAssets,
  isInvesting,
  layout = 'fixed',
  projectExpressingInterest,
  torch,
}) => {
  const { t } = useTranslate('torches')
  const { track } = useSafeAnalytics()
  const assets = campaignAssets.find((ca) => ca.fields.slug === torch?.campaignSlug)

  if (!assets) return null

  const redirectToInvestment = t('redirectToInvestment', 'You will be redirected to the investment site.')
  const learnMore = t('learnMore', 'Learn More')
  const continueToLabel = `${t('continueTo', 'Continue to')} ${torch?.title}`
  const people = t('people', 'people')
  const haveExpressedInterest = t('haveExpressedInterest', 'have expressed interest')
  const expressInterest = t('expressInterest', 'Express Interest')

  const { cardImage, shortDescription, tags } = assets.fields
  const investURLWithoutParams = torch?.investUrl?.split('?')?.[0]

  return (
    <CardWrapper
      cloudinaryPosterImage={torch?.posterPortraitCloudinaryPath}
      cloudinaryLandscapeImage={torch?.posterCloudinaryPath}
      contentfulImage={cardImage}
      layout={layout}
    >
      <div className="p-6 @4xl:max-w-[440px]">
        <HeadingXS weight="semibold" className="lg:photon-heading-sm">
          {torch?.title}
        </HeadingXS>
        {!!tags?.length && (
          <ul className="mt-2 flex">
            {tags.map((tag) => (
              <EyebrowSM
                as={(p) => <li {...p} />}
                key={tag.sys.id}
                weight="semibold"
                className="border-r px-2 first:pl-0 last:border-0"
              >
                {tag.fields.title}
              </EyebrowSM>
            ))}
          </ul>
        )}

        {shortDescription && (
          <TextBlock className="mt-6" document={shortDescription} optionOverrides={shortDescriptionOverride} />
        )}

        {isInvesting && investURLWithoutParams && (
          <div className="mt-6 flex flex-col gap-14 @lg:flex-row @lg:gap-4 @4xl:flex-col @4xl:gap-10">
            <TitleXS className="@lg:grow-1">{redirectToInvestment}</TitleXS>
            <AngelFundingLink
              className="whitespace-nowrap 2xl:w-auto"
              href={investURLWithoutParams}
              linkContext="torch-invest-card"
              projectSlug={torch?.campaignSlug}
              leavingMessage={torch?.interstitialDescription}
              leavingContinueLabel={continueToLabel}
              onClick={() => track('Torch Invest Card Clicked', { campaign: torch?.campaignSlug })}
            >
              <Button variant="offblack" className="flex w-full items-center justify-center @lg:w-auto">
                {learnMore}
                <ExternalLinkIcon color="white" className="ml-2" />
              </Button>
            </AngelFundingLink>
          </div>
        )}

        {!isInvesting && (
          <div className="mt-6 flex flex-col gap-6 @lg:flex-row @4xl:flex-col">
            <div className="shrink-0 @lg:grow-1">
              <TitleMD weight="semibold" className="mb-1">
                {formatNumberForDisplay({
                  count: projectExpressingInterest?.totalUniqueInterestUsers ?? 0,
                  format: '0,0',
                })}{' '}
                {people}
              </TitleMD>
              <TitleXS>{haveExpressedInterest}</TitleXS>
            </div>
            <LinkButton
              href={`/torches/${torch?.campaignSlug}`}
              variant="offblack"
              className="w-full self-start @lg:w-auto"
              aria-label={expressInterest}
              onClick={() => track('Torch Express Interest Card Clicked', { campaign: torch?.campaignSlug })}
            >
              {expressInterest}
            </LinkButton>
          </div>
        )}
      </div>
    </CardWrapper>
  )
}

export default TorchCard
